// buildShareClassInput.ts
import { ShareClassInput } from "_graphql-types-frontend/graphql";
import { ExtractedShareClass } from "./types";

// Number inputs hand back "" once cleared and every enum select uses "" for
// "None", so form state is not directly assignable to the Int/Float/Boolean
// scalars. Coerce here - the mutation rejects the whole share class otherwise.
const num = (value: unknown): number | null => {
  if (value === "" || value === null || value === undefined) return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const str = (value: unknown): string | null =>
  value === "" || value === undefined ? null : (value as string | null);

const bool = (value: unknown): boolean | null =>
  value === "" || value === undefined ? null : (value as boolean | null);

/**
 * Builds the mutation input field by field. Never spread form state into a
 * GraphQL input: types are erased at runtime, so any field the form carries
 * that the schema does not define goes over the wire and fails the whole
 * mutation (see .github/code-review-checklist.md item 12).
 */
export function buildShareClassInput(
  shareClass: ExtractedShareClass,
  investmentId: number
): ShareClassInput {
  const { investmentFees, liquidity, peLiquidity } = shareClass;

  return {
    investmentId,
    name: str(shareClass.name),
    status: str(shareClass.status),
    restricted: bool(shareClass.restricted),
    isRcgNegotiated: bool(shareClass.isRcgNegotiated),
    currencyDenominationEnumId: num(shareClass.currencyDenominationEnumId),
    otherFees: str(shareClass.otherFees),

    investmentFees: investmentFees && {
      mgmtFee: num(investmentFees.mgmtFee),
      mgmtFeeCalcEnumId: num(investmentFees.mgmtFeeCalcEnumId),
      mgmtFeeFreqEnumId: num(investmentFees.mgmtFeeFreqEnumId),
      incentiveFee: num(investmentFees.incentiveFee),
      incentiveFeeCalcEnumId: num(investmentFees.incentiveFeeCalcEnumId),
      incentiveFeeFreqEnumId: num(investmentFees.incentiveFeeFreqEnumId),
      otherFee: num(investmentFees.otherFee),
      otherFeeCalcEnumId: num(investmentFees.otherFeeCalcEnumId),
      otherFeeFreqEnumId: num(investmentFees.otherFeeFreqEnumId),
      highWaterMark: bool(investmentFees.highWaterMark),
      annualReset: bool(investmentFees.annualReset),
      hurdle: bool(investmentFees.hurdle),
      hurdleBenchmark: str(investmentFees.hurdleBenchmark),
      standardShareClassRates: bool(investmentFees.standardShareClassRates),
      feeStructureChanged: bool(investmentFees.feeStructureChanged),
      feeStructureChangedNotes: str(investmentFees.feeStructureChangedNotes),
      abnormalFees: bool(investmentFees.abnormalFees),
      abnormalFeesNotes: str(investmentFees.abnormalFeesNotes),
      peMgmtFeeCalcEnumId: num(investmentFees.peMgmtFeeCalcEnumId),
      peIncentiveFeeCalcEnumId: num(investmentFees.peIncentiveFeeCalcEnumId),
      peMgmtFeeDuringCommitment: num(investmentFees.peMgmtFeeDuringCommitment),
      peMgmtFeeDuringCommitmentCalcEnumId: num(
        investmentFees.peMgmtFeeDuringCommitmentCalcEnumId
      ),
      peMgmtFeeDuringHarvest: num(investmentFees.peMgmtFeeDuringHarvest),
      peMgmtFeeDuringHarvestCalcEnumId: num(
        investmentFees.peMgmtFeeDuringHarvestCalcEnumId
      ),
      hasMgmtFeeStepDown: bool(investmentFees.hasMgmtFeeStepDown),
      peMgmtFeeStepDownPercent: num(investmentFees.peMgmtFeeStepDownPercent),
      peMgmtFeeStepDownCalcEnumId: num(
        investmentFees.peMgmtFeeStepDownCalcEnumId
      ),
    },

    liquidity: liquidity && {
      hasGate: bool(liquidity.hasGate),
      gatePercentNav: num(liquidity.gatePercentNav),
      gatePercentCapital: num(liquidity.gatePercentCapital),
      exceptions: str(liquidity.exceptions),
      hasLockup: bool(liquidity.hasLockup),
      lockupValue: num(liquidity.lockupValue),
      lockupEndValueId: num(liquidity.lockupEndValueId),
      lockupEndBizDays: bool(liquidity.lockupEndBizDays),
      secondLockupEndValueId: num(liquidity.secondLockupEndValueId),
      secondLockupEndBizDays: bool(liquidity.secondLockupEndBizDays),
      subscriptionValue: num(liquidity.subscriptionValue),
      subscriptionUnitId: num(liquidity.subscriptionUnitId),
      liquidityValue: num(liquidity.liquidityValue),
      liquidityUnitId: num(liquidity.liquidityUnitId),
      cashAvailableDays: num(liquidity.cashAvailableDays),
      noticeValue: num(liquidity.noticeValue),
      noticeBizDays: bool(liquidity.noticeBizDays),
      hasHoldback: bool(liquidity.hasHoldback),
      holdbackPercent: num(liquidity.holdbackPercent),
      workoutId: num(liquidity.workoutId),
      modelingNotes: str(liquidity.modelingNotes),
      // not editable here, but the column is not nullable
      lockupMonthlyValue: num(liquidity.lockupMonthlyValue) ?? 0,
      liquidityMonthlyValue: num(liquidity.liquidityMonthlyValue) ?? 0,
    },

    peLiquidity: peLiquidity && {
      preferredReturn: num(peLiquidity.preferredReturn),
      isCompounded: bool(peLiquidity.isCompounded),
      compoundingPeriodId: num(peLiquidity.compoundingPeriodId),
      hasCatchupProvision: bool(peLiquidity.hasCatchupProvision),
      catchupPercent: num(peLiquidity.catchupPercent),
      optionOfExtensionId: num(peLiquidity.optionOfExtensionId),
    },
  };
}
